import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  Compass, Trophy, MagnifyingGlass, Eye, Megaphone, UsersThree, ShoppingCart,
  Key, Gear, SignOut, List, CaretLeft, Bell, Translate, Check, Sun, Moon,
  Lock, Robot, Target, ChartLine, Graph, Microphone, PaintBrush, FileText,
  Users, Storefront, Crosshair, Flask, BookOpen, ShieldCheck, VideoCamera,
  Lightbulb, ClipboardText, GitBranch, Broadcast, Binoculars, Database,
  Lightning, House, Signpost, Tray, ChartLineUp, Buildings, MagnifyingGlassPlus, CaretDown
} from '@phosphor-icons/react';
import { useState, useEffect } from 'react';
import { agentsAPI, notificationsAPI } from '../lib/api';
import { toast } from 'sonner';
import { GLIM, MartianWordmark } from '@/brand';
import { INTELLIGENCE_GROUPS, INTELLIGENCE_ITEMS, intelligenceItemActive, isIntelligenceItemLocked, LOCKED_NAV_TOAST } from '../founder/intelligenceNav';
import { Drawer, DrawerContent } from './ui/drawer';
import '../styles/martianWorkspace.css';

// ═══ PERSONA-FILTERED NAVIGATION ═══
const ICON_MAP = {
  home: Compass, som: Trophy, 'my-som': Trophy, seo: MagnifyingGlass, 'ai-traffic': Eye,
  'paid-ads': Megaphone, 'creator-intelligence': UsersThree, 'discovery-revenue': ShoppingCart,
  'connect-apis': Key, 'connect-platforms': Key, settings: Gear, 'client-manager': Users,
  'geo-report': Target, 'som-by-client': Trophy, reports: ChartLine,
  'content-intel': MagnifyingGlass, 'affiliate-revenue': ShoppingCart,
  'linkedin-revenue': Lightning, 'marketplace-card': Storefront, 'pitch-deck': FileText,
  'brand-enquiries': Bell, 'search-everywhere': Crosshair,
  // AF-3.16 — Brand Growth OS
  'growth-overview': Graph, 'growth-opportunities': Target, 'growth-experiments': Flask,
  'growth-learnings': BookOpen, 'growth-environment': ShieldCheck,
  'creator-command-center': VideoCamera,
  // AF-3.19 — AI Discovery pillar + Creator chain pages
  'ai-discovery': Binoculars, 'data-connections': Database,
  'creator-strategy': GitBranch, 'creator-ideas': Lightbulb, 'creator-review': ClipboardText,
  'creator-decisions': ClipboardText, 'creator-production': VideoCamera, 'creator-distribution': Broadcast,
  onboarding: Compass, 'real-brand-readiness': ShieldCheck,
  // AF-3.20.4 — Brand IA consolidation
  'workspace-overview': Compass,
  // Milestone 6 — the displaced BrandOverviewPage's own new nav entry
  'brand-executive-overview': ChartLine,
  // Milestone 7A — Company Context
  'company-context': BookOpen,
  // Brand Console -- Signals/Recommendations (existing AF-2.2/AF-2.5
  // backends, new frontend pages) + DATA's first real connector-backed
  // surfaces (existing intelligence_measurements pipeline, new frontend
  // pages).
  'growth-signals': Lightning, 'growth-recommendations': Lightbulb,
  'data-paid-media': Megaphone, 'data-ecommerce': ShoppingCart,
  // Raw Data Console -- reuses the existing FileText icon (already used
  // for 'pitch-deck') rather than adding a new icon import for one item.
  'ai-raw-data': FileText,
  // Search/Competitors DATA surfaces -- reuse existing icon imports
  // (MagnifyingGlass already used for 'seo'/'content-intel', UsersThree
  // already used for 'creator-intelligence') rather than adding new ones.
  'data-search': MagnifyingGlass, 'data-competitors': UsersThree,
  // Raw Data Console phase 2/4 -- reuse existing icon imports (ChartLine
  // already used for 'reports'/'brand-executive-overview', Graph already
  // used for 'growth-overview') rather than adding new ones.
  'growth-outcomes': ChartLine, 'data-entities': Graph,
  // Final completeness audit -- reuse existing icon imports (Crosshair
  // already used for 'search-everywhere', ClipboardText already used for
  // 'creator-review'/'creator-decisions') rather than adding new ones.
  'growth-diagnoses': Crosshair, 'growth-action-plans': ClipboardText,
  // AF-3.20.5.3 — Super Admin IA consolidation
  'creator-workspaces': VideoCamera,
  'admin-users': UsersThree,
  // Phase 1 -- the founder's six destinations
  'founder-briefing': House, 'founder-decisions': Signpost, 'founder-work': Tray,
  'founder-results': ChartLineUp, 'founder-business': Buildings, 'founder-evidence': MagnifyingGlassPlus,
  // Restored INTELLIGENCE group (see founder/intelligenceNav.js). Its
  // children reuse the existing icon keys above ('search-everywhere',
  // 'som', 'geo-report', 'ai-raw-data', 'data-*', 'growth-environment');
  // only the group itself and Creative Intelligence need new keys.
  'founder-intelligence': Binoculars, 'intel-creative': PaintBrush,
};

// Phase 1 (Founder-First UX) -- the brand persona sees the same six
// destinations (plus the expandable INTELLIGENCE group) everywhere, including the few pages still rendered in this
// legacy layout (Settings, onboarding). The backend pipeline pages are no
// longer founder navigation; see docs/product/phase1-ux-transformation-map.md.
const FOUNDER_NAV_ITEMS = [
  { key: 'founder-briefing', path: '/', label: 'Briefing' },
  // INTELLIGENCE -- an expandable group (progressive disclosure) holding
  // the specialist AI-search / search / commerce / paid / creative / market
  // surfaces. Same data as the founder rail's group (founder/intelligenceNav.js).
  { key: 'founder-intelligence', label: 'Intelligence', group: INTELLIGENCE_GROUPS },
  { key: 'founder-decisions', path: '/decisions', label: 'Decisions' },
  { key: 'founder-work', path: '/work', label: 'Work' },
  { key: 'founder-results', path: '/results', label: 'Results' },
  { key: 'founder-business', path: '/business', label: 'Your Business' },
  { key: 'founder-evidence', path: '/evidence', label: 'Evidence' },
];

// AF-3.19 — primary product navigation: MARTIAN > AI DISCOVERY / GROWTH /
// CREATOR. This replaces the AF-3.16 "BRAND GROWTH OS" + flat "MORE" dump
// with the three-pillar architecture the product is meant to feel like
// (spec: "the user should feel these are three connected product
// surfaces, not three unrelated products"). Legacy items that are real
// and DB-backed are re-homed under the pillar they conceptually belong
// to; items identified as fabricated/seeded in the AF-3.19 audit are
// intentionally NOT included here (see LEGACY_NAV_ITEMS below) rather
// than presented as if they were core product surfaces.
const AI_DISCOVERY_NAV_ITEMS = [
  { key: '_section_ai_discovery', section: 'AI DISCOVERY' },
  { key: 'ai-discovery', path: '/ai-discovery', label: 'AI Visibility' },
  { key: 'search-everywhere', path: '/search-everywhere', label: 'AI Answers & Mentions' },
  { key: 'som', path: '/som', label: 'Share of Model' },
  { key: 'geo-report', path: '/geo-report', label: 'Citations & Sources', badge: true },
  // Raw Data Console -- the real, already-existing GET /v2/executions/{id}
  // and GET /v2/citations/{id}/history routes, previously unconsumed by
  // any frontend page. Placed last in this pillar: it's the inspection
  // layer underneath the aggregate views above it, not a new capability.
  { key: 'ai-raw-data', path: '/ai-discovery/raw-data', label: 'AI Research Data' },
];

const GROWTH_NAV_ITEMS = [
  { key: '_section_growth', section: 'GROWTH' },
  { key: 'growth-overview', path: '/growth/overview', label: 'Command Center' },
  // Brand Console -- Signals (AF-2.2 SignalService) and Recommendations
  // (AF-2.5 RecommendationService) both already existed as real,
  // tenant/client-scoped backend services with a full REST surface under
  // /intelligence/{workspace_id}/signals and /recommendations; only the
  // dedicated brand-facing pages were missing.
  { key: 'growth-signals', path: '/growth/signals', label: 'Signals' },
  // Final completeness audit -- Diagnoses (AF-2.3 DiagnosisService) is the
  // real, deterministic causal-hypothesis step between Signals and
  // Opportunities that had a full, already-filterable
  // GET /intelligence/{workspace_id}/diagnoses route with zero frontend
  // consumer anywhere (not even a resolved id elsewhere). Action Plans
  // (AF-2.6 ActionPlanService) is the real governance/readiness/approval
  // step between Recommendations and Outcomes -- its own workspace-wide
  // route was only ever used internally (OutcomesPage.js's name
  // resolution), never shown as its own record set.
  { key: 'growth-diagnoses', path: '/growth/diagnoses', label: 'Diagnoses' },
  // P1 console polish (docs/product/intelligence-console-whole-chain-audit.md,
  // P1 #1; docs/product/intelligence-console-p1-polish-report.md) --
  // Opportunities moved to follow Diagnoses so the sidebar's own order
  // matches the real intelligence chain (SIGNAL -> DIAGNOSIS ->
  // OPPORTUNITY -> RECOMMENDATION -> ACTION PLAN). Navigation-order
  // change only -- same key, path, label, and position relative to
  // Recommendations/Action Plans.
  { key: 'growth-opportunities', path: '/growth/opportunities', label: 'Opportunities' },
  { key: 'growth-recommendations', path: '/growth/recommendations', label: 'Recommendations' },
  { key: 'growth-action-plans', path: '/growth/action-plans', label: 'Action Plans' },
  { key: 'growth-experiments', path: '/growth/experiments', label: 'Experiments' },
  { key: 'growth-learnings', path: '/growth/learnings', label: 'Learnings' },
  { key: 'growth-environment', path: '/growth/environment', label: 'Environment' },
  // Raw Data Console phase 2 -- Outcomes (AF-2.8 OutcomeService) is the
  // one notable backend-backed surface without a dedicated frontend
  // viewer (Signals/Recommendations/Opportunities/Learnings/Environment
  // all already have one). Consumes the existing GET
  // /intelligence/{workspace_id}/outcomes route, zero frontend consumer
  // until now -- no new backend behavior.
  { key: 'growth-outcomes', path: '/growth/outcomes', label: 'Outcomes' },
  { key: 'data-connections', path: '/growth/data-connections', label: 'Data Connections' },
];

// AF-3.19 P1 — the Creator chain frontend orphan gap. Command Center stays
// the AF-3.10 aggregation entry point; the five items below are new,
// real, route-backed pages over the previously frontend-less
// strategies/ideas/reviews/decisions/distribution chain (spec: "use
// existing endpoints, no duplicate computation in React").
const CREATOR_NAV_ITEMS = [
  { key: '_section_creator', section: 'CREATOR' },
  { key: 'creator-command-center', path: '/growth/creator-command-center', label: 'Command Center' },
  { key: 'creator-strategy', path: '/creator/strategy', label: 'Strategy' },
  { key: 'creator-ideas', path: '/creator/ideas', label: 'Ideas' },
  { key: 'creator-review', path: '/creator/review', label: 'Review' },
  { key: 'creator-decisions', path: '/creator/decisions', label: 'Decisions' },
  { key: 'creator-production', path: '/creator/production', label: 'Production / Video' },
  { key: 'creator-distribution', path: '/creator/distribution', label: 'Distribution' },
];

const GROWTH_OS_NAV_ITEMS = [...AI_DISCOVERY_NAV_ITEMS, ...GROWTH_NAV_ITEMS, ...CREATOR_NAV_ITEMS];

// AF-3.20.4 — Brand-only IA consolidation. The Brand sidebar gets its own
// top-level DATA section (locked spec: AI DISCOVERY -> GROWTH -> CREATOR
// -> DATA -> SYSTEM) rather than "Data Connections" living inside GROWTH.
// This reuses the exact same route/component (/growth/data-connections,
// DataConnectionsPage) -- no new page, no new route -- it is only
// re-presented under a different sidebar section for the brand persona
// specifically. Agency/admin's own nav (out of this stage's scope: "Refactor
// ONLY the Brand sidebar/navigation") is left byte-for-byte unchanged and
// still shows Data Connections inside GROWTH via the untouched
// GROWTH_OS_NAV_ITEMS spread below.
const BRAND_GROWTH_NAV_ITEMS = GROWTH_NAV_ITEMS.filter((item) => item.key !== 'data-connections');
const DATA_NAV_ITEMS = [
  { key: '_section_data', section: 'DATA' },
  { key: 'data-connections', path: '/growth/data-connections', label: 'Connections' },
  // Brand Console -- the first real DATA surfaces beyond the connection
  // manager itself: both pages consume ONLY the existing connector ->
  // intelligence_bridge -> intelligence_measurements pipeline (real
  // Google Ads/Meta/Shopify data when connected), never the legacy
  // fabricated /ads or /marketplace routes. Analytics/Search/Customers-
  // CRM are deliberately NOT added here -- no real backend data source
  // exists yet for any of them (see PaidMediaPage.js/EcommercePage.js's
  // own module docstrings for the audit this is based on).
  { key: 'data-paid-media', path: '/growth/paid-media', label: 'Paid Media' },
  { key: 'data-ecommerce', path: '/growth/ecommerce', label: 'Ecommerce' },
  // Next real DATA surfaces: Search consumes the existing DataForSEO ->
  // intelligence_bridge -> intelligence_measurements pipeline
  // (entity_type=keyword); Competitors consumes the existing, real,
  // tenant-scoped GET /v2/competitors/{id} route as a read-only viewer
  // (Company Context remains the one place to add/remove a competitor).
  { key: 'data-search', path: '/data/search', label: 'Search' },
  { key: 'data-competitors', path: '/data/competitors', label: 'Competitors' },
  // Raw Data Console phase 4 -- the real, already-existing GET
  // /v3/graph/entities/{id} route, previously only consumed internally
  // by other pages (Paid Media/Ecommerce/Search/Outcomes) to resolve a
  // name -- never shown as its own standalone record set until now.
  { key: 'data-entities', path: '/data/entities', label: 'Entities' },
];
// Milestone 6 (AI CGO Experience Integration) -- '/' is now Martian Home
// (SahibBriefingPage) for every brand tenant regardless of plan, not
// just free-plan (App.js's HomeRouter). Labeled "Your Briefing" at every
// plan now, not just the free-plan-only rename this item used to need
// (see the removed brandFree .map() override below -- no longer needed,
// the base label already says the right thing). The page BrandOverviewPage
// used to silently become once a brand activated now has its own real,
// permanent entry instead -- 'brand-executive-overview' below -- so it
// stays a genuinely reachable, deeper workspace surface rather than
// disappearing from navigation entirely.
const WORKSPACE_NAV_ITEMS = [
  { key: '_section_workspace', section: 'WORKSPACE' },
  { key: 'workspace-overview', path: '/', label: 'Your Briefing' },
  { key: 'brand-executive-overview', path: '/overview', label: 'Executive Overview' },
  // Milestone 7A -- Martian's foundational, brand-controlled understanding
  // of the business. Locked for free plan by construction (not in
  // brandFree's unlockedKeys below), same treatment as Executive Overview.
  { key: 'company-context', path: '/company-context', label: 'Company Context' },
  { key: 'onboarding', path: '/onboarding', label: 'Add a Brand' },
];

// AF-3.19 P0 — legacy items classified LEGACY/DUPLICATE/FABRICATED-SEEDED/
// NOT_READY in the consolidation report. None of these compete with the
// new pillars for primary attention; kept reachable (not deleted) under a
// single "MORE" section per persona, per "do not delete legacy
// functionality blindly." AI Traffic, Discovery-to-Revenue, and Creative
// Intelligence's scoring UI were converted to explicit Coming/Requires-
// Connection states at their own routes rather than removed outright —
// see AIDiscoveryComingSoonPage / CreativeIntelligencePage.
const LEGACY_NAV_ITEMS = (extra = []) => [
  { key: '_section_legacy', section: 'MORE (LEGACY)' },
  { key: 'seo', path: '/seo', label: 'GEO and SEO' },
  { key: 'ai-traffic', path: '/ai-traffic', label: 'AI Traffic' },
  { key: 'paid-ads', path: '/ads', label: 'Paid Ads' },
  { key: 'creative-intelligence', path: '/creative-intelligence', label: 'Creative Intelligence (legacy scoring)' },
  { key: 'agents', path: '/agents', label: 'AI Agents' },
  { key: 'creator-intelligence', path: '/creators', label: 'Creator Seeding (legacy)' },
  { key: 'discovery-revenue', path: '/dtri', label: 'Discovery-to-Revenue' },
  ...extra,
];

// AF-3.20.5.3 — Super Admin IA consolidation. Every path below reuses an
// existing, already-tested route -- no new page, no new backend call,
// no access change (ALLOWED_ROUTES.admin/master stays '*'; this is a
// sidebar declutter, not a permissions change). Two destinations named
// in the authorization's preferred structure have no real frontend
// route to reuse and are deliberately NOT included, rather than
// fabricated: "Audit Logs" (masterAPI.auditLog() exists in api.js but
// is never rendered by any page) and a literal "Credential References"
// viewer (the AF-3.17.1 credentialReferencesAPI is only ever called
// internally by DataConnectionsPage's own connect flow, not exposed as
// its own page). "Credential References" below reuses /vault (VaultPage)
// instead -- the legacy raw-key vault, a different system from the
// newer metadata-only CredentialReference model, documented here and in
// AF-3.20.5.3_GLOBAL_THEME_ADMIN_IA_CLOSURE_REPORT.md Section 1/4.
const ADMIN_PLATFORM_NAV_ITEMS = [
  { key: '_section_admin_platform', section: 'PLATFORM' },
  { key: 'home', path: '/', label: 'Overview' },
  { key: 'client-manager', path: '/clients', label: 'Brand Workspaces' },
  // Creator Persona Portal's real home (CreatorWorkspacePage) -- per
  // AF-3.20.5.3 Part D, this single entry point replaces the old
  // "CREATOR PERSONA PORTAL" section (which listed the portal's home
  // plus 6 of its own sub-pages directly in admin's primary nav). The
  // portal itself is untouched, not redesigned, and its 6 sub-routes
  // remain fully reachable by URL -- just no longer individually listed
  // here (see report Section 3 for the full disposition).
  { key: 'creator-workspaces', path: '/creator-workspace', label: 'Creator Workspaces' },
  // No standalone "user list" page exists apart from Settings' own Team
  // tab -- this and SYSTEM > Settings below intentionally share the
  // /settings route under two different labels (a documented gap, not a
  // duplicate-route bug: see report Section 1).
  { key: 'admin-users', path: '/settings', label: 'Users' },
  { key: 'data-connections', path: '/growth/data-connections', label: 'Data Connections' },
];

const ADMIN_INTELLIGENCE_NAV_ITEMS = [
  { key: '_section_admin_intelligence', section: 'INTELLIGENCE' },
  { key: 'ai-discovery', path: '/ai-discovery', label: 'AI Discovery' },
  { key: 'growth-overview', path: '/growth/overview', label: 'Growth' },
  { key: 'creator-command-center', path: '/growth/creator-command-center', label: 'Creator Intelligence' },
];

const ADMIN_SECURITY_NAV_ITEMS = [
  { key: '_section_admin_security', section: 'SECURITY' },
  { key: 'connect-apis', path: '/vault', label: 'Credential References' },
  { key: 'real-brand-readiness', path: '/admin/real-brand-readiness', label: 'Real-Brand Readiness' },
];

// Locked funnel & Free Evaluation Workspace -- items a free-plan brand
// tenant can't use yet stay LISTED (with a lock icon and no navigation,
// see the nav item click handler below) rather than removed outright,
// per the explicit "show what you'll unlock, don't just hide it"
// instruction. Only real, working destinations (Briefing, adding a
// brand, Data Connections, Settings) are left clickable by default.
// A tenant-scoped product_audit_scopes grant from /auth/me can open
// founder Intelligence pages without changing plan (see intelligenceNav).
const lockNavItems = (items, unlockedKeys) =>
  items.map((item) => (item.section || item.divider || unlockedKeys.includes(item.key) ? item : { ...item, locked: true }));

const NAV_CONFIGS = {
  // AF-3.20.4 — Brand IA consolidation. Locked hierarchy: WORKSPACE ->
  // AI DISCOVERY -> GROWTH -> CREATOR -> DATA -> SYSTEM. No "MORE (LEGACY)"
  // section, no Creator Persona Portal exposure (brand nav never had one).
  // Legacy items (Discovery Radar, GEO/SEO, AI Traffic, Paid Ads, Creative
  // Intelligence, AI Agents, Creator Seeding, Discovery-to-Revenue, the
  // /vault key manager) are intentionally not listed here -- their routes
  // and source files are untouched and remain directly reachable by URL;
  // see AF-3.20.4_INFORMATION_ARCHITECTURE_REPORT.md Section 4 for the
  // full disposition of every removed nav entry.
  brand: [
    ...FOUNDER_NAV_ITEMS,
    { key: '_divider_founder', divider: true },
    { key: 'settings', path: '/settings', label: 'Settings' },
  ],
  agency: [
    { key: 'home', path: '/', label: 'Portfolio Overview' },
    { key: 'client-manager', path: '/clients', label: 'Client Manager' },
    ...GROWTH_OS_NAV_ITEMS,
    ...LEGACY_NAV_ITEMS([{ key: 'discovery-radar', path: '/discovery-radar', label: 'Discovery Radar (legacy)' }]),
    { key: '_divider_1', divider: true },
    { key: 'connect-apis', path: '/vault', label: 'Connect APIs' },
    { key: 'settings', path: '/settings', label: 'Settings' },
  ],
  creator: [
    { key: 'home', path: '/', label: 'My Dashboard' },
    { key: 'my-som', path: '/som', label: 'My SoM Score' },
    { key: 'content-intel', path: '/creator/content-intelligence', label: 'Content Intelligence' },
    { key: 'affiliate-revenue', path: '/creator/affiliate-revenue', label: 'Affiliate Revenue' },
    { key: 'linkedin-revenue', path: '/creator/linkedin-revenue', label: 'LinkedIn Revenue' },
    { key: 'marketplace-card', path: '/creator/marketplace-card', label: 'My Marketplace Card' },
    { key: 'pitch-deck', path: '/creator/pitch-deck', label: 'AI Pitch Deck' },
    { key: 'brand-enquiries', path: '/creator/brand-enquiries', label: 'Brand Enquiries', badge: true },
    { key: '_divider_1', divider: true },
    { key: 'connect-platforms', path: '/vault', label: 'Connect Platforms' },
    { key: 'settings', path: '/settings', label: 'Settings' },
  ],
  // AF-3.20.5.3 — Super Admin IA consolidation: PLATFORM -> INTELLIGENCE
  // -> SECURITY -> SYSTEM. No "MORE (LEGACY)" section, no "CREATOR
  // PERSONA PORTAL" section exposing 7 sub-items -- see the disposition
  // table in AF-3.20.5.3_GLOBAL_THEME_ADMIN_IA_CLOSURE_REPORT.md Section
  // 4 for exactly where every removed item's route/component now lives
  // (all preserved, none deleted). ALLOWED_ROUTES.admin stays '*' --
  // this changes what's LISTED, never what's REACHABLE.
  admin: [
    ...ADMIN_PLATFORM_NAV_ITEMS,
    ...ADMIN_INTELLIGENCE_NAV_ITEMS,
    ...ADMIN_SECURITY_NAV_ITEMS,
    { key: '_section_system', section: 'SYSTEM' },
    { key: 'admin-settings', path: '/settings', label: 'Settings' },
  ],
  // Free Evaluation Workspace -- same items, same order as `brand` above
  // (so activating later doesn't reshuffle anything the user has
  // already learned), just locked except for what a free-plan tenant
  // can genuinely use today: the Briefing itself ("workspace-overview"),
  // adding a brand, connecting data, and Settings. Kept as the LAST key
  // here (not inserted next to `brand`) so every existing source-text
  // test that slices from "brand: [" to "agency: [" to isolate the
  // brand config's own block keeps working unchanged.
  //
  // AI Visibility ("ai-discovery") was briefly left unlocked here as
  // "one real AI Discovery view" -- found (real user testing, not
  // assumed) to be wrong: a fresh free tenant has no ai_responses yet,
  // so that "real view" rendered as raw UNKNOWN/NOT_AVAILABLE/
  // INSUFFICIENT_EVIDENCE backend states (AIDiscoveryCommandCenterPage.js
  // -- intentionally honest for the full workspace, never fabricated,
  // but wrong as a free evaluation user's first impression). Locked like
  // every other module; the curated real signal a free user gets is
  // SahibBriefingPage's own "What Martian found" section instead.
  //
  // Do not unlock Intelligence here for all free tenants. Controlled
  // product-audit access is a tenant-scoped server flag
  // (product_audit_scopes), not a change to this entitlement source.
  brandFree: [
    // Milestone 6 -- 'workspace-overview' is already labeled 'Your
    // Briefing' at the WORKSPACE_NAV_ITEMS source now (it's Martian Home at
    // every plan), so the label-override .map() this used to need is
    // gone. 'brand-executive-overview' (the new /overview entry) is
    // deliberately NOT in unlockedKeys -- it renders locked for a
    // free-plan tenant exactly like every other non-briefing item, and
    // FREE_PLAN_LOCKED_PATHS (derived from this exact config below) picks
    // it up automatically, so direct-URL access is blocked too. No new
    // capability is unlocked for free plan by this milestone.
    ...lockNavItems(WORKSPACE_NAV_ITEMS, ['workspace-overview', 'onboarding']),
    ...lockNavItems(AI_DISCOVERY_NAV_ITEMS, []),
    ...lockNavItems(BRAND_GROWTH_NAV_ITEMS, []),
    ...lockNavItems(CREATOR_NAV_ITEMS, []),
    ...lockNavItems(DATA_NAV_ITEMS, ['data-connections']),
    { key: '_section_system', section: 'SYSTEM' },
    { key: 'settings', path: '/settings', label: 'Settings' },
  ],
};

// Free Evaluation Workspace -- the sidebar's own lock icon only stops a
// click; it never stopped direct navigation (typing the URL, or a
// stale bookmark) from reaching the raw page underneath. Derived from
// NAV_CONFIGS.brandFree itself (not a second hand-maintained list) so
// it can never drift from what the sidebar actually shows as locked.
export const FREE_PLAN_LOCKED_PATHS = NAV_CONFIGS.brandFree
  .filter((item) => item.locked && item.path)
  .map((item) => item.path);

// Routes allowed per persona (for route guard)
// AF-3.16 — Brand Growth OS routes, shared across brand/agency personas.
const GROWTH_OS_ROUTES = [
  '/ai-discovery', '/ai-discovery/raw-data', '/growth/overview', '/growth/opportunities', '/growth/signals', '/growth/diagnoses', '/growth/recommendations', '/growth/action-plans', '/growth/experiments', '/growth/learnings', '/growth/environment', '/growth/outcomes', '/growth/creator-command-center', '/growth/data-connections', '/growth/paid-media', '/growth/ecommerce', '/data/search', '/data/competitors', '/data/entities',
  '/creator/strategy', '/creator/ideas', '/creator/review', '/creator/decisions', '/creator/production', '/creator/distribution',
];

export const ALLOWED_ROUTES = {
  brand: ['/', '/som', '/som/prompts', '/som/history', '/som/usage', '/seo', '/ai-traffic', '/ads', '/creators', '/dtri', '/vault', '/settings', '/geo-report', '/benchmark', '/search-score', '/voice', '/entity', '/visual-search', '/creative-intelligence', '/marketplace', '/website', '/agents', '/search-everywhere', ...GROWTH_OS_ROUTES],
  agency: ['/', '/clients', '/geo-report', '/som', '/som/prompts', '/som/history', '/som/usage', '/dtri', '/ads', '/creators', '/vault', '/settings', '/seo', '/search', '/benchmark', '/search-score', '/voice', '/entity', '/ai-traffic', '/creative-intelligence', '/marketplace', '/website', '/agents', '/discovery-radar', '/search-everywhere', ...GROWTH_OS_ROUTES],
  creator: ['/', '/som', '/som/prompts', '/som/history', '/som/usage', '/creator-workspace', '/creator/content-intelligence', '/creator/affiliate-revenue', '/creator/linkedin-revenue', '/creator/marketplace-card', '/creator/pitch-deck', '/creator/brand-enquiries', '/vault', '/settings', '/dtri'],
  admin: ['*'],
  master: ['*'],
};

export const Sidebar = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [langOpen, setLangOpen] = useState(false);
  // Expandable nav groups (INTELLIGENCE). A group opens by itself when the
  // current page is one of its children; the user can still toggle it.
  const [openGroups, setOpenGroups] = useState({});

  const isRTL = i18n.language === 'ar';
  // AF-3.20.5.1 -- a missing/malformed tenant_type must fail toward the
  // restricted "brand" nav, never toward "admin" (the only persona whose
  // nav contains both "MORE (LEGACY)" and "CREATOR PERSONA PORTAL"
  // together -- exactly the symptom reported in AF-3.20.5.1). This does
  // not affect any account with a real tenant_type value, including
  // genuine admin/master accounts (isPlatformAdmin below checks
  // user?.tenant_type directly, not this fallback).
  const tenantType = user?.tenant_type || 'brand';

  const isPlatformAdmin =
    user?.tenant_type === 'admin' ||
    user?.tenant_type === 'master' ||
    user?.role === 'super_admin';

  // A free-plan brand sees the same approved console navigation as every
  // other brand (so INTELLIGENCE stays fully visible); what its plan gates is
  // shown with the lock convention below, driven by FREE_PLAN_LOCKED_PATHS.
  // NAV_CONFIGS.brandFree is no longer rendered -- it remains solely as the
  // entitlement source FREE_PLAN_LOCKED_PATHS is derived from.
  const navItems = isPlatformAdmin
    ? NAV_CONFIGS.admin
    : (NAV_CONFIGS[tenantType] || NAV_CONFIGS.admin);

  useEffect(() => {
    const load = () => {
      agentsAPI.pendingCount().then(r => setPendingCount(r.data.count)).catch(() => {});
      notificationsAPI.unreadCount().then(r => setUnreadNotifs(r.data.count)).catch(() => {});
    };
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);

  const toggleNotifications = async () => {
    if (!notifOpen) { try { const r = await notificationsAPI.list(); setNotifications(r.data); } catch {} }
    setNotifOpen(!notifOpen); setLangOpen(false);
  };
  const markAllRead = async () => { try { await notificationsAPI.markAllRead(); setUnreadNotifs(0); } catch {} };
  const switchLang = (lang) => { i18n.changeLanguage(lang); localStorage.setItem('nx_lang', lang); setLangOpen(false); };
  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <>
      <button data-testid="mobile-menu-toggle" className={`fixed top-4 ${isRTL ? 'right-4' : 'left-4'} z-50 md:hidden p-2 rounded-[var(--radius-button)] bg-[var(--surface)] border border-[var(--border)] shadow-[var(--shadow-card)] text-[var(--text-secondary)]`} onClick={() => setMobileOpen(!mobileOpen)}>
        <List size={20} weight="bold" />
      </button>
      {mobileOpen && <div className="fixed inset-0 bg-black/30 z-40 md:hidden" onClick={() => setMobileOpen(false)} />}

      <aside data-testid="sidebar-nav" className={`nx-sidebar ${mobileOpen ? 'open' : ''}`} style={{ width: collapsed ? 64 : 240 }}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-4 border-b border-[var(--border)]">
            {!collapsed && (
              <div className="flex items-center gap-2">
                <GLIM size={28} decorative />
                <MartianWordmark size="sm" />
              </div>
            )}
            {collapsed && <div className="mx-auto"><GLIM size={28} label="Martian" /></div>}
            <button data-testid="sidebar-collapse-btn" className="hidden md:flex p-1 rounded hover:bg-[var(--canvas-tertiary)] text-[var(--text-tertiary)]" onClick={() => setCollapsed(!collapsed)}>
              <CaretLeft size={14} className={`transition-transform duration-200 ${collapsed ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {/* Controls: Notif + Lang + Theme */}
          {!collapsed && (
            <div className="flex items-center gap-1 px-3 pt-2 pb-1">
              <div className="relative">
                <button data-testid="notification-bell" onClick={toggleNotifications} className="p-1.5 rounded hover:bg-[var(--canvas-tertiary)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] relative">
                  <Bell size={16} weight={unreadNotifs > 0 ? 'fill' : 'regular'} />
                  {unreadNotifs > 0 && <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 text-[8px] font-mono bg-[var(--error)] text-white rounded-full flex items-center justify-center">{unreadNotifs > 9 ? '9+' : unreadNotifs}</span>}
                </button>
                {notifOpen && (
                  <div className={`absolute top-full ${isRTL ? 'right-0' : 'left-0'} mt-1 w-64 bg-[var(--surface-raised)] border border-[var(--border-light)] rounded-[var(--radius-card-soft)] shadow-[var(--shadow-dropdown)] z-50 max-h-60 overflow-y-auto`}>
                    <div className="flex items-center justify-between p-2 border-b border-[var(--border-light)]">
                      <span className="text-[9px] text-[var(--text-primary)] font-bold">Notifications</span>
                      {unreadNotifs > 0 && <button onClick={markAllRead} className="text-[8px] text-[var(--accent)]">Mark all read</button>}
                    </div>
                    {notifications.length === 0 ? <p className="p-3 text-[9px] text-[var(--text-tertiary)] text-center">No notifications</p> :
                      notifications.slice(0, 5).map(n => (
                        <div key={n.id} className={`p-2 border-b border-[var(--border-light)] ${!n.read ? 'bg-[var(--accent-light)]' : ''}`}>
                          <p className="text-[9px] text-[var(--text-primary)]">{n.title}</p>
                          <p className="text-[8px] text-[var(--text-tertiary)]">{n.message?.substring(0, 60)}</p>
                        </div>
                      ))
                    }
                  </div>
                )}
              </div>
              <div className="relative">
                <button data-testid="language-switcher" onClick={() => { setLangOpen(!langOpen); setNotifOpen(false); }} className="p-1.5 rounded hover:bg-[var(--canvas-tertiary)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] flex items-center gap-0.5">
                  <Translate size={16} /><span className="text-[8px] font-mono uppercase">{i18n.language}</span>
                </button>
                {langOpen && (
                  <div className={`absolute top-full ${isRTL ? 'right-0' : 'left-0'} mt-1 w-28 bg-[var(--surface-raised)] border border-[var(--border-light)] rounded-[var(--radius-card-soft)] shadow-[var(--shadow-dropdown)] z-50`}>
                    {[{ code: 'en', label: 'English' }, { code: 'ar', label: 'العربية' }].map(lang => (
                      <button key={lang.code} data-testid={`lang-${lang.code}`} onClick={() => switchLang(lang.code)}
                        className="w-full flex items-center gap-1.5 px-2 py-1.5 text-[10px] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--canvas-tertiary)]">
                        {i18n.language === lang.code && <Check size={10} className="text-[var(--accent)]" />}{lang.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button data-testid="theme-toggle" onClick={toggleTheme} className="p-1.5 rounded hover:bg-[var(--canvas-tertiary)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]">
                {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
              </button>
            </div>
          )}

          {/* ═══ PERSONA-FILTERED NAVIGATION ═══ */}
          <nav className="flex-1 min-h-0 py-2 overflow-y-auto" data-testid="sidebar-navigation">
            {navItems.map((item) => {
              if (item.divider) return <div key={item.key} className="my-2 mx-3 h-px bg-[var(--border)]" />;
              if (item.section) return !collapsed ? <p key={item.key} className="px-4 mt-4 mb-1 text-[10px] font-mono uppercase tracking-[0.08em] text-[var(--text-tertiary)]">{item.section}</p> : <div key={item.key} className="my-2 mx-3 h-px bg-[var(--border)]" />;
              if (item.group) {
                const GroupIcon = ICON_MAP[item.key] || Compass;
                const groupActive = item.group.some((g) => g.items.some((c) => intelligenceItemActive(c, location.pathname)));
                const isOpen = !collapsed && (openGroups[item.key] ?? groupActive);
                return (
                  <div key={item.key}>
                    <button data-testid={`nav-${item.key}`} aria-expanded={isOpen}
                      className={`nx-nav-item w-full ${isRTL ? 'text-right' : 'text-left'} ${groupActive ? 'active' : ''}`}
                      onClick={() => {
                        if (collapsed) { setCollapsed(false); setOpenGroups((s) => ({ ...s, [item.key]: true })); return; }
                        setOpenGroups((s) => ({ ...s, [item.key]: !isOpen }));
                      }}>
                      <GroupIcon size={17} weight={groupActive ? 'fill' : 'regular'} />
                      {!collapsed && <span className="text-[12px]">{item.label}</span>}
                      {!collapsed && <CaretDown size={11} className={`ml-auto text-[var(--text-tertiary)] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />}
                    </button>
                    {isOpen && item.group.map((g) => (
                      <div key={g.key} data-testid={`nav-group-${g.key}`}>
                        <p className="pl-9 pr-4 mt-2 mb-0.5 text-[9px] font-mono uppercase tracking-[0.08em] text-[var(--text-tertiary)]">{g.label}</p>
                        {g.items.map((child) => {
                          const ChildIcon = ICON_MAP[child.key] || Compass;
                          const childActive = intelligenceItemActive(child, location.pathname);
                          if (isIntelligenceItemLocked(child, user, FREE_PLAN_LOCKED_PATHS)) {
                            return (
                              <button key={child.key} data-testid={`nav-${child.key}`} data-locked="true" aria-disabled="true"
                                title={LOCKED_NAV_TOAST}
                                className={`nx-nav-item w-full !pl-9 opacity-50 cursor-default ${isRTL ? 'text-right' : 'text-left'}`}
                                onClick={() => toast(LOCKED_NAV_TOAST, { description: child.label })}>
                                <ChildIcon size={15} weight="regular" />
                                <span className="text-[12px]">{child.label}</span>
                                <Lock size={11} className="ml-auto text-[var(--text-tertiary)]" aria-hidden="true" />
                                <span className="sr-only"> (locked: unlocks in your full Martian workspace)</span>
                              </button>
                            );
                          }
                          return (
                            <button key={child.key} data-testid={`nav-${child.key}`}
                              className={`nx-nav-item w-full !pl-9 ${isRTL ? 'text-right' : 'text-left'} ${childActive ? 'active' : ''}`}
                              onClick={() => { navigate(child.to); setMobileOpen(false); }}>
                              <ChildIcon size={15} weight={childActive ? 'fill' : 'regular'} />
                              <span className="text-[12px]">{child.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                );
              }
              const Icon = ICON_MAP[item.key] || Compass;
              const isActive = item.path === '/som'
                ? (location.pathname === '/som' || location.pathname.startsWith('/som/'))
                : location.pathname === item.path;
              // Free Evaluation Workspace -- a locked item stays visible
              // (so the user can see what's ahead) but never navigates;
              // it explains itself instead of landing on an empty page.
              if (item.locked) {
                return (
                  <button key={item.key} data-testid={`nav-${item.key}`}
                    className={`nx-nav-item w-full opacity-50 cursor-default ${isRTL ? 'text-right' : 'text-left'}`}
                    onClick={() => toast('This unlocks in your full Martian workspace', { description: item.label })}>
                    <Icon size={17} weight="regular" />
                    {!collapsed && <span className="text-[12px]">{item.label}</span>}
                    {!collapsed && <Lock size={11} className="ml-auto text-[var(--text-tertiary)]" />}
                  </button>
                );
              }
              return (
                <button key={item.key} data-testid={`nav-${item.key}`}
                  className={`nx-nav-item w-full ${isRTL ? 'text-right' : 'text-left'} ${isActive ? 'active' : ''}`}
                  onClick={() => { navigate(item.path); setMobileOpen(false); }}>
                  <Icon size={17} weight={isActive ? 'fill' : 'regular'} />
                  {!collapsed && <span className="text-[12px]">{item.label}</span>}
                  {!collapsed && item.badge && item.key === 'geo-report' && pendingCount > 0 && (
                    <span className="ml-auto px-1.5 py-0.5 text-[9px] font-mono bg-[var(--error)] text-white rounded-full leading-none">{pendingCount}</span>
                  )}
                  {!collapsed && item.badge && item.key === 'brand-enquiries' && unreadNotifs > 0 && (
                    <span className="ml-auto px-1.5 py-0.5 text-[9px] font-mono bg-[var(--accent)] text-white rounded-full leading-none">{unreadNotifs}</span>
                  )}
                  {!collapsed && item.key === 'linkedin-revenue' && (
                    <Lock size={10} className="ml-auto text-[var(--text-tertiary)]" />
                  )}
                </button>
              );
            })}
          </nav>

          {/* User */}
          <div className="p-3 border-t border-[var(--border)]">
            {!collapsed && user && (
              <div className="flex items-center gap-2.5 mb-2">
                <div className="w-7 h-7 rounded-full bg-[var(--accent-light)] flex items-center justify-center text-[var(--accent)] text-[11px] font-medium">
                  {user.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] text-[var(--text-primary)] font-medium truncate">{user.name}</p>
                  <p className="text-[9px] text-[var(--text-tertiary)] truncate capitalize">{tenantType} workspace</p>
                </div>
              </div>
            )}
            <button data-testid="logout-button" className={`nx-nav-item w-full ${isRTL ? 'text-right' : 'text-left'} !text-[var(--error)] hover:!text-[var(--error)] hover:!bg-[var(--error-bg)]`} onClick={handleLogout}>
              <SignOut size={17} />{!collapsed && <span className="text-[12px]">Logout</span>}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Milestone 6.1 (Explicit Workspace Context) -- workspaceContext is
// optional and additive: every existing caller of DashboardLayout never
// passes it, so this renders exactly as before for them. When set (only
// by the /workspace/:clientId route, after it has already verified real
// ownership via POST /workspace-context/enter), a persistent, unmissable
// banner sits above the page content -- the Admin sidebar stays exactly
// as-is around it (v1 scope: the surrounding shell doesn't change,
// only what's rendered inside it), so "Back to Admin Workspace" and
// every other admin destination stay one click away throughout.
// Martian Centre Workspace UX Refactor -- `rightPanel` is optional and
// purely additive: every one of this component's ~70 existing callers
// never passes it, so they render byte-for-byte as before (Sidebar +
// single <main> column, no third column at all). Only a caller that
// explicitly opts in (SahibBriefingPage, for its "Talk to Martian" slot)
// gets the third column. This is the real architectural primitive for
// the eventual right rail -- not a one-off inline div on a single page
// -- so any future page can adopt the same slot without re-deriving the
// responsive behavior. Desktop: LEFT RAIL | CENTRE | RIGHT.
//
// Milestone 12 -- below the breakpoint, the right panel no longer just
// disappears: it becomes a real bottom drawer (shadcn/vaul Drawer,
// already installed and otherwise unused -- reused rather than
// hand-rolling a second sheet/overlay primitive), triggered by a small
// floating "Talk to Martian" button. The centre column is never crushed
// (the drawer is an overlay, not a squeezed third column) and never
// permanently hidden either.
//
// Milestone 13 -- the breakpoint moved from `xl` (1280px) to `lg`
// (1024px), and the panel narrowed from 300px to 280px, after a real
// recorded session showed the panel never once appearing: a genuinely
// desktop-class browser window, at real-world Windows display scaling,
// reported an effective CSS viewport under 1280px -- so "the right rail
// is always available on desktop" was silently false. `lg` gives real
// scaled desktop windows enough headroom while still handing off to the
// drawer well before the centre column would feel cramped.
// A brand founder on an INTELLIGENCE page (rendered in this legacy layout)
// gets a way back to the founder experience: the Briefing and Decisions. It
// is a breadcrumb over the same intelligenceNav data, not a second nav.
export function IntelligenceTrail() {
  const location = useLocation();
  const { user } = useAuth();
  if ((user?.tenant_type || 'brand') !== 'brand') return null;
  const item = INTELLIGENCE_ITEMS.find((i) => intelligenceItemActive(i, location.pathname));
  if (!item) return null;
  const link = 'rounded-sm hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:[box-shadow:var(--shadow-focus-ring)]';
  return (
    <nav aria-label="Breadcrumb" data-testid="intelligence-trail" className="mb-3 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[12px] text-[var(--text-tertiary)]">
      <Link to="/" className={link} data-testid="intelligence-trail-briefing">Briefing</Link>
      <span aria-hidden="true">/</span>
      <span>Intelligence</span>
      <span aria-hidden="true">/</span>
      <span aria-current="page" className="text-[var(--text-secondary)]">{item.label}</span>
      <Link to="/decisions" className={`${link} ml-auto`} data-testid="intelligence-trail-decisions">Decisions</Link>
    </nav>
  );
}

export const DashboardLayout = ({ children, workspaceContext, rightPanel }) => {
  const navigate = useNavigate();
  const [talkDrawerOpen, setTalkDrawerOpen] = useState(false);
  // Milestone 15 (Visual Identity Rebuild) -- `.martian-workspace` scopes
  // the new retro-computing token layer to exactly this tree, only when
  // rightPanel is set (today: SahibBriefingPage only -- see
  // martianWorkspace.css's own docstring for why this lives outside
  // index.css entirely). bg-[var(--martian-paper,...)] falls back to the
  // existing global token so every other page rendering DashboardLayout
  // without rightPanel is byte-for-byte unchanged.
  return (
    <div className={`flex min-h-screen bg-[var(--martian-paper,var(--canvas-secondary))] ${rightPanel ? 'martian-workspace' : ''}`}>
      <Sidebar />
      <main className="nx-main flex-1 min-w-0 flex flex-col lg:flex-row lg:items-start gap-5 p-5 pt-16 md:p-6" data-testid="main-content">
        <div className="flex-1 min-w-0">
          <IntelligenceTrail />
          {workspaceContext && (
            <div
              data-testid="workspace-context-banner"
              className="flex items-center justify-between gap-3 flex-wrap mb-4 px-4 py-2.5 rounded-[var(--martian-radius,var(--radius-card))] bg-[var(--martian-violet,var(--accent))] text-white"
            >
              <p className="text-[12.5px] font-medium">
                Viewing workspace: <strong>{workspaceContext.name}</strong>
              </p>
              <button
                data-testid="back-to-admin-workspace"
                onClick={() => navigate('/')}
                className="flex items-center gap-1 text-[12px] font-semibold opacity-90 hover:opacity-100"
              >
                <CaretLeft size={12} weight="bold" /> Back to Admin Workspace
              </button>
            </div>
          )}
          {children}
        </div>
        {rightPanel && (
          <>
            {/* Milestone 16 -- the one real separator between centre and
                 right is a single left rule here, not a border the panel
                 draws around itself (TalkToMartianPanel.js dropped its own
                 box for exactly this reason: two adjacent bordered
                 rectangles read as "two cards", not one workspace). */}
            <aside className="hidden lg:block w-[280px] shrink-0 lg:border-l lg:border-[var(--martian-rule,var(--border))]" data-testid="right-panel">
              {rightPanel}
            </aside>
            <button
              onClick={() => setTalkDrawerOpen(true)}
              data-testid="right-panel-drawer-trigger"
              className="lg:hidden fixed bottom-5 right-5 z-40 flex items-center gap-1.5 bg-[var(--martian-violet,var(--text-primary))] text-white text-[12px] font-semibold px-3.5 py-2.5 rounded-[var(--martian-radius,var(--radius-button))] border-2 border-[var(--martian-rule-strong,transparent)] [box-shadow:var(--martian-shadow,var(--shadow-hover))]"
            >
              <GLIM size={20} decorative /> Talk to Martian
            </button>
            <Drawer open={talkDrawerOpen} onOpenChange={setTalkDrawerOpen}>
              {/* Milestone 29 -- real bug found in a live browser walkthrough
                   (not visible from source/tests alone): the base Drawer
                   primitive's own class is `h-auto` with no overflow
                   handling, so a long reply (a cross-channel summary is
                   comfortably longer than 85vh on a real phone-width
                   viewport) pushed the input past the drawer's own
                   max-height with no way to scroll back down to it --
                   the input became genuinely unreachable. `overflow-y-auto`
                   is the smallest fix: once real content exceeds max-h,
                   the drawer itself scrolls as one unit instead of
                   silently clipping past its own edge. */}
              <DrawerContent data-testid="right-panel-drawer" className="max-h-[85vh] overflow-y-auto p-4 pt-2 bg-[var(--martian-surface,var(--surface))]">
                {rightPanel}
              </DrawerContent>
            </Drawer>
          </>
        )}
      </main>
    </div>
  );
};
